<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="950" scrollable>
    <v-card>
      <v-card-title class="teal darken-1 white--text">
        <v-icon left dark>mdi-eye</v-icon>
        Preview del Post
        <v-spacer></v-spacer>
        <v-btn icon dark @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-5">
        <!-- Categoría -->
        <v-chip v-if="post.categoryName" small color="teal lighten-4" class="mb-3">
          <v-icon left small v-if="post.categoryIcon">{{ post.categoryIcon }}</v-icon>
          {{ post.categoryName }}
        </v-chip>

        <!-- Grilla 2x2 dinámica -->
        <v-row>
          <v-col v-for="item in visibleSections" :key="item.section"
            :cols="sectionCols(item.section)">
            <div class="section-box" :style="{ backgroundColor: item.color }">
              <!-- TEXT -->
              <div v-if="item.section === 'text'">
                <h2 class="mb-2">{{ post.title }}</h2>
                <div class="post-body" v-html="post.body"></div>
                <div class="mt-3">
                  <v-icon small color="red">mdi-heart</v-icon>
                  <span class="caption ml-1">{{ post.likes_count || 0 }} Me Gusta</span>
                </div>
              </div>

              <!-- IMAGES -->
              <div v-if="item.section === 'images'">
                <v-img :src="selectedImage" max-height="280" contain
                  class="rounded grey lighten-3 mb-2"
                  style="cursor: pointer;"
                  @click="dialogFullImage = true"></v-img>
                <v-row dense v-if="post.imageUrls.length > 1">
                  <v-col cols="3" v-for="(url, i) in post.imageUrls" :key="i">
                    <v-card outlined
                      :class="['thumb-card', { 'selected-thumb': selectedIndex === i }]"
                      @click="selectedIndex = i"
                      style="cursor: pointer;">
                      <v-img :src="url" height="50" contain></v-img>
                    </v-card>
                  </v-col>
                </v-row>
              </div>

              <!-- VIDEO -->
              <div v-if="item.section === 'video'">
                <iframe :src="embedVideoUrl" width="100%" height="250" frameborder="0"
                  allowfullscreen style="border-radius: 8px;"></iframe>
              </div>

              <!-- COMMENTS -->
              <div v-if="item.section === 'comments'">
                <p class="subtitle-2 mb-1"><v-icon small>mdi-comment-multiple</v-icon> Comentarios</p>
                <v-divider class="mb-2"></v-divider>
                <p class="caption grey--text">
                  {{ post.comments_count || 0 }} comentarios — visible para usuarios.
                </p>
                <div class="comment-placeholder">
                  <v-icon small color="grey">mdi-account-circle</v-icon>
                  <span class="caption grey--text ml-1">Escribir un comentario...</span>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="grey lighten-4">
        <span class="caption grey--text">
          <v-icon small>mdi-calendar</v-icon>
          {{ post.created_at ? formatDate(post.created_at) : 'Ahora' }}
        </span>
        <v-spacer></v-spacer>
        <v-btn text @click="$emit('input', false)">Cerrar</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Dialog imagen completa -->
    <v-dialog v-model="dialogFullImage" max-width="1000">
      <v-card color="black" flat>
        <v-btn icon dark absolute top right @click="dialogFullImage = false" style="z-index:1;">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-img :src="selectedImage" contain max-height="80vh"></v-img>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
export default {
  name: 'ForumPostPreview',
  props: {
    value: { type: Boolean, default: false },
    post: { type: Object, default: () => ({}) },
    previewMode: { type: String, default: 'local' }
  },
  data() {
    return {
      selectedIndex: 0,
      dialogFullImage: false
    };
  },
  watch: {
    value(val) {
      if (val) {
        this.selectedIndex = 0;
        this.dialogFullImage = false;
      }
    }
  },
  computed: {
    layoutOrder() {
      const defaultLayout = [
        { section: 'text', color: '#E3F2FD' },
        { section: 'images', color: '#E8F5E9' },
        { section: 'video', color: '#FFF3E0' },
        { section: 'comments', color: '#F3E5F5' }
      ];
      return this.post.layout_order || defaultLayout;
    },
    hasImages() {
      return this.post.imageUrls && this.post.imageUrls.length > 0;
    },
    hasVideo() {
      return !!this.post.video_url;
    },
    // Solo mostrar secciones que tienen contenido (excepto text y comments que siempre se muestran)
    visibleSections() {
      return this.layoutOrder.filter(item => {
        if (item.section === 'text') return true;
        if (item.section === 'comments') return true;
        if (item.section === 'images') return this.hasImages;
        if (item.section === 'video') return this.hasVideo;
        return false;
      });
    },
    selectedImage() {
      if (!this.hasImages) return '';
      return this.post.imageUrls[this.selectedIndex] || this.post.imageUrls[0];
    },
    embedVideoUrl() {
      if (!this.post.video_url) return '';
      const url = this.post.video_url;
      const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
      if (ytMatch) return 'https://www.youtube.com/embed/' + ytMatch[1];
      const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
      if (vimeoMatch) return 'https://player.vimeo.com/video/' + vimeoMatch[1];
      return url;
    }
  },
  methods: {
    // Calcular cols dinámicamente: si hay secciones ocultas, las visibles se expanden
    sectionCols(section) {
      const total = this.visibleSections.length;
      if (total <= 1) return 12;
      if (total === 2) return 6;
      if (total === 3) {
        // Las primeras 2 arriba (6 cada una), la tercera abajo (12 full)
        const idx = this.visibleSections.findIndex(item => item.section === section);
        return idx < 2 ? 6 : 12;
      }
      // 4 secciones: grilla 2x2
      return 6;
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleDateString('es', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
  }
};
</script>

<style scoped>
.section-box {
  padding: 16px;
  border-radius: 8px;
  min-height: 120px;
  height: 100%;
}
.post-body {
  line-height: 1.7;
}
.post-body p {
  margin-bottom: 8px;
}
.thumb-card {
  opacity: 0.6;
  transition: all 0.2s ease;
}
.thumb-card:hover {
  opacity: 0.9;
}
.selected-thumb {
  border: 2px solid #009688 !important;
  opacity: 1 !important;
}
.comment-placeholder {
  padding: 10px;
  border: 1px dashed #ccc;
  border-radius: 6px;
  margin-top: 8px;
}
</style>
